import type { SyntheticEvent } from 'react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';
import debounce from 'lodash/debounce';
import { SchemaTypesUtil } from '@axiom/types';
import set from 'lodash/set';
import type { z } from 'zod';

import { FormSchemaUtil } from '../../../utils/form-schema-util';
import { DesignSysEnvUtil } from '../../../utils/design-sys-env-util';

import { invokeFormContext } from './useFormContext';
import type { FormProps, FormPropsChildren } from './form.types';

export const RawForm = <Schema extends z.ZodTypeAny>({
  children,
  dirty,
  handleReset,
  handleSubmit,
  isSubmitting,
  isValid,
  name,
  onChange,
  schema,
  setTouched,
  setFieldTouched,
  setValues,
  submitCount,
  submitOnChange,
  values,
}: {
  children: FormProps<Schema>['children'];
  dirty: FormPropsChildren<Schema>['dirty'];
  handleReset: (e?: SyntheticEvent<unknown>) => void;
  handleSubmit: (e?: SyntheticEvent<unknown>) => void | Promise<void>;
  isSubmitting: boolean;
  isValid: FormPropsChildren<Schema>['isValid'];
  name: string;
  onChange?: FormProps<Schema>['onChange'];
  schema: FormProps<Schema>['schema'];
  setTouched: FormPropsChildren<Schema>['setTouched'];
  setFieldTouched: FormPropsChildren<Schema>['setFieldTouched'];
  setValues: FormPropsChildren<Schema>['setValues'];
  submitCount: number;
  submitOnChange?: boolean;
  values: FormPropsChildren<Schema>['values'];
}) => {
  const FormContext = invokeFormContext();
  const formik = useFormikContext();

  const formattedSchema = useMemo(
    () => SchemaTypesUtil.format(schema),
    [schema]
  );

  const debouncedSubmit = useCallback(
    debounce(async () => formik.submitForm(), 500),
    [formik.submitForm]
  );

  const debounceChange = useCallback(
    debounce((propName: string, changedValue: unknown) => {
      if (onChange) {
        const newChangedValue = set({}, propName, changedValue);

        onChange(newChangedValue as Partial<Schema>);
      }
    }, DesignSysEnvUtil.typingDebounce),
    [onChange, formik.values, values]
  );

  useEffect(() => {
    if (submitOnChange && formik.values !== formik.initialValues) {
      void debouncedSubmit();
    }
  }, [formik.values]);

  let formStatus = 'form-active';
  if (submitCount > 0 && !isSubmitting) {
    formStatus = 'form-submitted';
  }
  const formProviderValue = useMemo(
    () => ({
      getSchemaProperty: (propName: string) => {
        return FormSchemaUtil.findNode(formattedSchema, propName);
      },
      fireReset: handleReset,
      fireSubmit: handleSubmit,
      fireChange: debounceChange,
      values,
    }),
    [handleReset, handleSubmit, values]
  );

  return (
    <div data-test={name}>
      <FormContext.Provider value={formProviderValue}>
        <div data-test={formStatus}>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <form noValidate onSubmit={handleSubmit}>
            {children({
              dirty,
              fireReset: handleReset,
              fireSubmit: handleSubmit,
              fireChange: (field, changedValue) => {
                debounceChange(field, changedValue);
              },
              isValid,
              setTouched,
              setFieldTouched,
              setValues,
              submitCount,
              values,
            })}
          </form>
        </div>
      </FormContext.Provider>
    </div>
  );
};
